import { TableRow } from '@mui/material';
import TableCell from '@mui/material/TableCell';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { AuthContext, AuthData } from '../../context/auth';
import { ClaimContext, ClaimData } from '../../context/claim';
import { ConfigContext, ConfigData } from '../../context/config';
import { PolicyContext, PolicyData } from '../../context/policy';
import { SubClaimResponse } from '../../types/claim';
import { tableStyles } from '../../util/styles';
import {
	getComparator,
	HeadCell,
	Order,
	stableSort,
} from '../../util/tableUtil';
import { capitalize, formatCurrency } from '../../util/util';
import { StyledEmptyMessage } from '../common';
import SortableTable from '../SortableTable';
import { getSubclaimDisplayName } from './util';

interface Props {
	authData: AuthData;
	policyData: PolicyData;
	claimData: ClaimData;
	configData: ConfigData;
}

interface Data {
	subclaimId: string;
	dateCreated: string;
	lossReserve: string;
	expenseReserve: string;
	status: string;
	link: string;
	displayName: string;
}

function createData(
	subclaimId: string,
	dateCreated: string,
	lossReserve: string,
	expenseReserve: string,
	status: string,
	link: string,
	displayName: string,
): Data {
	return {
		subclaimId,
		displayName,
		dateCreated,
		lossReserve,
		expenseReserve,
		status,
		link,
	};
}

const SubclaimsTable = (props: Props) => {
	const { claimData, policyData, authData, configData } = props;
	const { claim } = claimData;
	const { subClaims } = claim;
	const { policy } = policyData;
	const [order, setOrder] = React.useState<Order>('asc');
	const [orderBy, setOrderBy] = React.useState<string>('subclaimId');
	const classes = tableStyles();

	const emptyTable = !subClaims.length ? (
		<StyledEmptyMessage>No Subclaims</StyledEmptyMessage>
	) : null;

	const headCells: HeadCell[] = [
		{ id: 'subclaimId', rightAlign: false, label: 'Subclaim Id' },
		{ id: 'perilName', rightAlign: true, label: 'Peril Name' },
		{ id: 'dateCreated', rightAlign: true, label: 'Date Created' },
		{ id: 'lossReserve', rightAlign: true, label: 'Loss Reserve' },
		{ id: 'expenseReserve', rightAlign: true, label: 'Expense Reserve' },
		{ id: 'status', rightAlign: true, label: 'Status' },
	];

	const rows: any = subClaims.map(
		(subClaim: SubClaimResponse, index: number) => {
			const id = subClaim.locator;
			const dateCreated = subClaim.createdTimestamp;
			const lossReserve = parseFloat(subClaim.lossReserve.currentAmount);
			const expenseReserve = parseFloat(subClaim.expenseReserve.currentAmount);
			const status = subClaim.currentStatus;
			const reserveCurrency = policy?.currency;
			const displayName = getSubclaimDisplayName(subClaim, policy, configData);

			return createData(
				id,
				dateCreated,
				formatCurrency(Math.abs(lossReserve), reserveCurrency),
				formatCurrency(Math.abs(expenseReserve), reserveCurrency),
				capitalize(status),
				`/policy/${policy.locator}/claims/${claim.locator}/subclaims/${subClaim.locator}`,
				displayName,
			);
		},
	);

	const rowElements = stableSort(rows, getComparator(order, orderBy)).map(
		(row: any, index: number) => {
			return (
				<TableRow
					hover
					tabIndex={-1}
					key={row.subclaimId}
					component={Link}
					to={row.link}
					className={classes.tableRow}
				>
					<TableCell scope='row'>{row.subclaimId}</TableCell>
					<TableCell align='right'>{row.displayName}</TableCell>
					<TableCell align='right'>
						{authData.getFormattedDate(row.dateCreated)}
					</TableCell>
					<TableCell align='right'>{row.lossReserve}</TableCell>
					<TableCell align='right'>{row.expenseReserve}</TableCell>
					<TableCell align='right'>{row.status}</TableCell>
				</TableRow>
			);
		},
	);

	return (
		<SortableTable
			rows={rowElements}
			headCells={headCells}
			additionalMsg={emptyTable}
			order={order}
			setOrder={setOrder}
			orderBy={orderBy}
			setOrderBy={setOrderBy}
		/>
	);
};

function withContext(Component: any) {
	return (
		props: Omit<Props, 'authData' | 'claimData' | 'policyData' | 'configData'>,
	) => (
		<AuthContext.Consumer>
			{(authData) => (
				<PolicyContext.Consumer>
					{(policyData) => (
						<ClaimContext.Consumer>
							{(claimData) => (
								<ConfigContext.Consumer>
									{(configData) => (
										<Component
											{...props}
											authData={authData}
											policyData={policyData}
											claimData={claimData}
											configData={configData}
										/>
									)}
								</ConfigContext.Consumer>
							)}
						</ClaimContext.Consumer>
					)}
				</PolicyContext.Consumer>
			)}
		</AuthContext.Consumer>
	);
}

export default withContext(SubclaimsTable);
